import {
    ChangeDetectorRef,
    DestroyRef,
    ElementRef,
    QueryList
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent } from 'rxjs';
import { auditTime, startWith } from 'rxjs/operators';
import { animationFrameScheduler } from 'rxjs';

export function initFlyoutObserver(
    sectionWrappers: QueryList<ElementRef>,
    onUpdate: () => void,
    destroyRef: DestroyRef
): IntersectionObserver {
    const observer = new IntersectionObserver(() => onUpdate(), {
        threshold: [0, 0.1, 0.5, 1]
    });

    fromEvent(document, 'scroll', { capture: true } as AddEventListenerOptions)
        .pipe(
            auditTime(0, animationFrameScheduler),
            takeUntilDestroyed(destroyRef)
        )
        .subscribe(() => onUpdate());

    sectionWrappers.changes
        .pipe(startWith(sectionWrappers), takeUntilDestroyed(destroyRef))
        .subscribe((list: QueryList<ElementRef>) => {
            list.forEach((ref) => observer.observe(ref.nativeElement));
            onUpdate();
        });

    return observer;
}

export function computeFlyoutState(sectionWrappers: QueryList<ElementRef>): {
    activeFlyoutIndex: number;
    flyoutFlipUp: boolean;
} {
    const TARGET_Y = 250;
    let bestIdx = -1;
    let bestDist = Infinity;

    const scrollEl = document.querySelector(
        'mat-sidenav-content'
    ) as HTMLElement | null;
    const atBottom = scrollEl
        ? scrollEl.scrollTop + scrollEl.clientHeight >=
          scrollEl.scrollHeight - 50
        : false;

    sectionWrappers.forEach((ref, i) => {
        const subTitle = ref.nativeElement.querySelector(
            '.sub-title'
        ) as HTMLElement | null;
        if (!subTitle) return;
        const rect = subTitle.getBoundingClientRect();
        if (rect.top < -30 || rect.top > window.innerHeight) return;
        if (atBottom) {
            bestIdx = i;
        } else {
            const dist = Math.abs(rect.top - TARGET_Y);
            if (dist < bestDist) {
                bestDist = dist;
                bestIdx = i;
            }
        }
    });

    let flyoutFlipUp = false;
    if (bestIdx >= 0) {
        const wrapper = sectionWrappers.get(bestIdx);
        if (wrapper) {
            const subTitle = wrapper.nativeElement.querySelector(
                '.sub-title'
            ) as HTMLElement | null;
            const titleTop = subTitle
                ? subTitle.getBoundingClientRect().top
                : wrapper.nativeElement.getBoundingClientRect().top;
            const footerEl = document.querySelector(
                'app-footer'
            ) as HTMLElement | null;
            const footerTop = footerEl
                ? Math.min(
                      footerEl.getBoundingClientRect().top,
                      window.innerHeight
                  )
                : window.innerHeight;
            flyoutFlipUp = footerTop - titleTop < 360;
        }
    }

    return { activeFlyoutIndex: bestIdx, flyoutFlipUp };
}

export function positionFlipUpTooltip(
    activeFlyoutIndex: number,
    flyoutFlipUp: boolean,
    sectionWrappers: QueryList<ElementRef>
): void {
    if (activeFlyoutIndex < 0 || !flyoutFlipUp) return;
    const wrapper = sectionWrappers.get(activeFlyoutIndex);
    if (!wrapper) return;
    const subTitle = wrapper.nativeElement.querySelector(
        '.sub-title'
    ) as HTMLElement | null;
    if (!subTitle) return;
    const tooltip = subTitle.querySelector(
        '.sub-title-tooltip'
    ) as HTMLElement | null;
    if (!tooltip) return;

    const subTitleRect = subTitle.getBoundingClientRect();

    const headerEl = document.querySelector('app-header') as HTMLElement | null;
    const headerBottom = headerEl
        ? Math.max(headerEl.getBoundingClientRect().bottom, 0)
        : 0;
    const footerEl = document.querySelector('app-footer') as HTMLElement | null;
    const footerTop = footerEl
        ? Math.min(footerEl.getBoundingClientRect().top, window.innerHeight)
        : window.innerHeight;

    const availableSpace = footerTop - headerBottom - 20;
    const tooltipHeight = Math.min(
        tooltip.scrollHeight || 520,
        availableSpace,
        520
    );

    const idealBottom = subTitleRect.top - 10;
    const idealTop = idealBottom - tooltipHeight;
    const clampedTopViewport = Math.max(idealTop, headerBottom + 10);
    const clampedBottomViewport = Math.min(
        clampedTopViewport + tooltipHeight,
        footerTop - 10
    );
    const finalTopViewport = clampedBottomViewport - tooltipHeight;

    tooltip.style.setProperty('max-height', `${tooltipHeight}px`, 'important');
    tooltip.style.setProperty(
        'top',
        `${finalTopViewport - subTitleRect.top}px`,
        'important'
    );
}

export function applyFlyoutUpdate(
    sectionWrappers: QueryList<ElementRef>,
    cdr: ChangeDetectorRef,
    positionCallback: () => void,
    applyState: (state: ReturnType<typeof computeFlyoutState>) => void
): void {
    const result = computeFlyoutState(sectionWrappers);
    applyState(result);
    cdr.detectChanges();
    requestAnimationFrame(positionCallback);
}
